import { BASE_URL, get, post, useFetch } from 'utils/sdk';
import { Currency } from './types/store';
import { PurchaseStatus } from './types/order';
import { EPriceType } from 'components/NewProductStepper/ProductBaseForm/constants';
import { IProductPrice } from './product_sdk';
import { EProductFieldType, ProductCategory } from './types/product';

export const addProductToCard = ({
  productId,
  quantity,
  deliveryDate,
  price,
  extraFields,
  note,
  priceType,
  productPriceId,
}: {
  note: string;
  productId: number;
  quantity: number;
  deliveryDate: Date;
  price: number;
  extraFields?: Array<{ productField: number; value: string }>;
  priceType: EPriceType;
  productPriceId?: number;
}) => {
  return post(`${BASE_URL}services/order/add/`, {
    product_id: productId,
    quantity,
    delivery_date: deliveryDate,
    note,
    price,
    price_type: priceType,
    extra_fields: extraFields?.map(({ productField, value }) => ({
      product_field_id: productField,
      value,
    })),
    product_price_id: productPriceId,
  });
};

export const updateOrderProduct = ({
  orderProductId,
  productId,
  quantity,
  deliveryDate,
  price,
  priceType,
  extraFields,
  note,
  productPriceId,
}: {
  orderProductId: number;
  note: string;
  productId: number;
  quantity: number;
  deliveryDate: Date;
  price: number;
  priceType: EPriceType;
  extraFields?: Array<{ productField: number; value: string }>;
  productPriceId?: number;
}) => {
  return post(`${BASE_URL}services/orderproduct/${orderProductId}/update/`, {
    product_id: productId,
    quantity,
    delivery_date: deliveryDate,
    note,
    price,
    price_type: priceType,
    extra_fields: extraFields?.map(({ productField, value }) => ({
      product_field_id: productField,
      value,
    })),
    product_price_id: productPriceId,
  });
};

export interface IGuestCartDetail {
  is_ready: boolean; // return true all purcahses are ready
  has_error: boolean;
  id: number;
  order_number?: string;
  guest_id: number;
  guest_name?: string;
  guest_email?: string;
  guest_phone?: string;
  store_id: number;
  store_name: string;
  currency: Currency;
  is_locked: boolean;
  total_amount: number;
  total_convenience_fees: number;
  guest_total: number;
  order_product_quantity: number;
  order_products: Array<{
    id: number;
    quantity: number;
    price: string;
    amount: number;
    convenience_fee: number;
    guest_amount: number;
    delivery_date: Date;
    note: string;
    is_preorder: boolean;
    product: {
      id: number;
      quantity: number;
      has_quantity: number;
    };
    product_info: {
      product_name: string;
      price: number;
      image: string;
      requires_approval: boolean;
      price_type: EPriceType;
      is_early_checkin: boolean;
      is_late_checkout: boolean;
      product_price?: IProductPrice;
    };
    extra_field_values?: Array<{
      label: string;
      value: string;
      product_field_id: number;
    }>;
    purchase_status?: PurchaseStatus;
    purchase_failure_message?: string | null;
    product_price: IProductPrice | undefined;
    is_purchasable_off_platform: boolean;
  }>;
  has_off_platform_products: boolean;
  is_purchasable_off_platform: boolean;
  off_platform_total: number;
}

export const useGuestCartDetail = ({ storeId }: { storeId: number | null }) =>
  useFetch<IGuestCartDetail>(storeId ? `services/guest/store-cart/${storeId}/detail/` : undefined);

export interface IGuestCheckoutStoreSummary {
  id: number;
  guest_id: number;
  guest_name?: string;
  guest_email?: string;
  guest_phone?: string;
  store_id: number;
  store_name: string;
  currency: Currency;
  is_locked: boolean;
  total_amount: number;
  total_convenience_fees: number;
  guest_total: number;
  order_product_quantity: number;
  is_purchasable_off_platform: boolean;
  off_platform_total: number;
}

export const useGuestCheckoutStoreSummary = ({
  storeId,
  orderId,
  enabled,
}: {
  storeId: number;
  orderId: number;
  enabled;
}) =>
  useFetch<IGuestCheckoutStoreSummary>(
    enabled
      ? `services/guest/store-checkout-summary/${storeId}/order/${orderId}/detail/`
      : undefined,
  );

export const getGuestOrderDetails = ({ orderId }: { orderId: number }) =>
  get<IGuestCartDetail>(`${BASE_URL}services/guest/order/${orderId}/detail/`);

export const useGuestOrderDetails = ({ orderId }: { orderId: number }) =>
  useFetch<IGuestCartDetail>(`services/guest/order/${orderId}/detail/`);

export interface IRecommendedItem {
  id: number;
  store_id: number;
  product_name: string;
  category: ProductCategory;
  description: string;
  cancellation_policy?: string | null;
  price: number;
  price_type: EPriceType;
  quantity: number;
  has_quantity: boolean;
  preorder_by: number;
  image: string;
  requires_approval: boolean;
  subcategory: 'Add-ons';
  external_link: string | null;
  instructions: string | null;
  is_late_checkout: boolean;
  is_early_checkin: boolean;
  product_prices: Array<IProductPrice>;
}
export const useRecommendedItems = ({ orderId }: { orderId: number }) =>
  useFetch<Array<IRecommendedItem>>(`services/guest/order/${orderId}/recommend/`);

export interface IGuestStoreDetails {
  id: number;
  store_name: string;
  image: string;
  description: string;
  currency: string;
  is_active: boolean;
  is_visible: boolean;
  enterprise_info: {
    id?: number;
    image?: string;
    company_name?: string;
    about?: string;
    subdomain?: string;
    is_enabled?: boolean;
    is_subdomain?: boolean;
  };
}

export const useGuestStoreDetails = ({ storeId }: { storeId: number | null }) =>
  useFetch<IGuestStoreDetails>(storeId ? `services/guest/store/${storeId}/` : undefined);

export interface IGuestProduct {
  id: number;
  product_name: string;
  image: string;
  store_arrangement: number;
  category: string;
  subcategory?: string;
  service_type?: string;
  preorder_by?: number;
  requires_approval: boolean;
  description?: string;
  cancellation_policy?: string | null;
  instructions?: string;
  quantity?: number;
  has_quantity: boolean;
  price: number;
  price_type: string;
  affiliate_link?: string;
  external_link?: string;
  is_request: boolean;
  is_preorder: boolean;
  is_local: boolean;
  is_external_link: boolean;
  is_late_checkout: boolean;
  is_early_checkin: boolean;
  show_delivery_date: boolean;
  extra_fields?: Array<{
    id: number;
    label: string;
    field_type?: EProductFieldType;
  }>;
  product_prices?: Array<{
    id: number;
    label?: string;
    hour_label?: string;
    time_label?: string;
    price: number;
    arrangement: number;
    is_variable_price: boolean;
  }>;
}

export const useGuestProductList = ({ storeId }: { storeId: number | null }) =>
  useFetch<Array<IGuestProduct>>(storeId ? `services/guest/store/${storeId}/products/` : undefined);
